import React, { useState, useEffect } from "react";
import { collection, addDoc, getDoc, doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import "../css/homepage.css";
import { generateWarrantyId } from "../utils/warrantyUtils";
import { sendWarrantySMS } from "../services/smsService";

function Homepage() {
  const [formData, setFormData] = useState({
    consumerName: "",
    dealerAddress: "",
    mobileNumber: "",
    address: "",
    dealerName: "",
    termsAccepted: false,
  });

  const [submitted, setSubmitted] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [warrantyCardLabel, setWarrantyCardLabel] = useState(
    "Customer Warranty Card",
  );
  const [websiteUrl, setWebsiteUrl] = useState("www.mcan.in");

  // Fetch company logo and settings from Firestore
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsDoc = await getDoc(
          doc(db, "app-settings", "app-settings"),
        );
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          setLogoUrl(data.companyLogo || "");
          setWarrantyCardLabel(data["customer-warranty-label"]);
          setWebsiteUrl(data.website || "www.mcan.in");
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.termsAccepted) {
      alert("Please accept Terms & Conditions");
      return;
    }

    setLoading(true);

    try {
      const mobileKey = formData.mobileNumber.replace(/\D/g, "");

      // Calculate warranty end date (7 years from now)
      const registrationDate = new Date();
      const warrantyEndDate = new Date(registrationDate);
      warrantyEndDate.setFullYear(warrantyEndDate.getFullYear() + 7);

      // Generate unique warranty ID
      const newWarrantyId = await generateWarrantyId();

      // Use addDoc to create a new unique document every time
      const docRef = await addDoc(collection(db, "warranty-card-details"), {
        consumerName: formData.consumerName,
        dealerAddress: formData.dealerAddress,
        mobileNumber: mobileKey,
        address: formData.address,
        dealerName: formData.dealerName,
        termsAccepted: true,
        createdAt: serverTimestamp(),
        warrantyEndDate: warrantyEndDate,
        warrantyId: newWarrantyId,
        smsStatus: "pending"
      });

      // Prepare data for SMS
      const warrantyData = {
        consumerName: formData.consumerName,
        warrantyId: newWarrantyId,
        mobileNumber: mobileKey,
        // Format to standard DD/MM/YYYY
        warrantyStartDate: registrationDate.toLocaleDateString('en-GB'),
        warrantyEndDate: warrantyEndDate.toLocaleDateString('en-GB')
      };

      // Trigger SMS asynchronously without blocking the user response
      sendWarrantySMS(warrantyData)
        .then(async (smsResult) => {
          if (smsResult.success) {
            await updateDoc(docRef, {
              smsStatus: "success",
              smsSentAt: serverTimestamp()
            });
          } else {
            await updateDoc(docRef, {
              smsStatus: "failed"
            });
          }
        })
        .catch(error => console.error("Error in SMS flow:", error));

      setSubmitted(true);
    } catch (error) {
      console.error("Firestore Error:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="warranty-overlay">
      <div className="warranty-container">
        {!submitted ? (
          <div className="warranty-card">
            {/* Logo Section */}
            <div className="logo-section">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Company Logo"
                  className="company-logo"
                />
              ) : (
                <div className="logo-placeholder">
                  <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                    <rect width="60" height="60" rx="12" fill="#4F46E5" />
                    <path
                      d="M20 30L27 37L40 23"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </div>

            {/* Company Info Section */}
            <div className="company-info">
              <div className="iso-badge">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7z" />
                </svg>
                ISO 9001:2015 Certified Company
              </div>

              <div className="company-links">
                <a
                  href={websiteUrl.startsWith("http") ? websiteUrl : `https://${websiteUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="company-link"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="2" y1="12" x2="22" y2="12" />
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                  {websiteUrl}
                </a>

                <a href="mailto:info@mcan.in" className="company-link">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 4h16v16H4z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  info@mcan.in
                </a>
              </div>
            </div>

            <h1 className="warranty-title">{warrantyCardLabel}</h1>
            <p className="warranty-subtitle">
              Please fill in your details to register your warranty
            </p>

            <form onSubmit={handleSubmit} className="warranty-form">
              <div className="form-group">
                <label className="form-label">Consumer Name</label>
                <input
                  className="form-input"
                  type="text"
                  name="consumerName"
                  placeholder="Enter your full name"
                  value={formData.consumerName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Mobile Number</label>
                <input
                  className="form-input"
                  type="tel"
                  name="mobileNumber"
                  placeholder="Enter 10-digit mobile number"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  pattern="[0-9]{10}"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Address</label>
                <textarea
                  className="form-textarea"
                  name="address"
                  placeholder="Enter your complete address"
                  value={formData.address}
                  onChange={handleChange}
                  rows="3"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Dealer Name</label>
                <input
                  className="form-input"
                  type="text"
                  name="dealerName"
                  placeholder="Enter dealer name"
                  value={formData.dealerName}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Dealer Address</label>
                <textarea
                  className="form-textarea"
                  name="dealerAddress"
                  placeholder="Enter dealer address"
                  value={formData.dealerAddress}
                  onChange={handleChange}
                  rows="3"
                />
              </div>

              <div className="checkbox-wrapper">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="termsAccepted"
                    checked={formData.termsAccepted}
                    onChange={handleChange}
                    className="checkbox-input"
                  />
                  <span className="checkbox-text">
                    I accept the{" "}
                    <a
                      href="/terms-and-conditions"
                      rel="noopener noreferrer"
                      className="terms-link"
                    >
                      Terms & Conditions
                    </a>
                  </span>
                </label>
              </div>

              <button
                type="submit"
                className="submit-button"
                disabled={loading}
              >
                {loading ? (
                  <span className="loading-spinner"></span>
                ) : (
                  "Submit Warranty Card"
                )}
              </button>
            </form>

            {/* Divider and Customer Care Buttons */}
            <div className="divider-section">
              <div className="divider">
                <span className="divider-text">Need Help?</span>
              </div>
              <div className="customer-care-buttons">
                <a
                  href="tel:+919427487947"
                  className="customer-care-button call-button"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                  Call Us
                </a>

                <a
                  href="https://wa.me/919427487947"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="customer-care-button whatsapp-button"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                  </svg>
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        ) : (
          <div className="success-card">
            <div className="success-icon">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                <circle cx="40" cy="40" r="40" fill="#10B981" />
                <path
                  d="M25 40L35 50L55 30"
                  stroke="white"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className="success-title">Successfully Submitted!</h2>
            <p className="success-message">
              Your warranty card has been registered successfully. You will
              receive a confirmation message shortly.
            </p>
            <button
              onClick={() => {
                setSubmitted(false);
                setFormData({
                  consumerName: "",
                  dealerAddress: "",
                  mobileNumber: "",
                  address: "",
                  dealerName: "",
                  termsAccepted: false,
                });
              }}
              className="back-button"
            >
              Submit Another
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Homepage;
